import type {
  ActionItem,
  MeetingSummary,
  MeetingAgreement,
  AgreementContradiction,
  DocumentContradiction,
} from '@consigli/types';

// ===== Shared Interfaces =====

export interface ParsedTranscriptEntry {
  startTime: string;
  endTime: string;
  /** Display value: assigned user name when assigned, else the pipeline label. */
  speaker: string;
  text: string;
  timestamp: number;
  /** Segment UUID — required to reassign the speaker. */
  segmentId?: string;
  /** Raw pipeline speaker label (null once assigned) — used to group batch reassignments. */
  speakerName?: string | null;
  /** Set when the segment is linked to a real MeetingUser. */
  assignedMeetingUserId?: string | null;
}

export interface MeetingParticipant {
  id: string;
  email: string;
  name?: string | null;
  role: string;
}

export const getNonOrganizerParticipants = <T extends { role: string }>(
  participants: T[] | undefined | null,
): T[] => (participants ?? []).filter((p) => p.role !== 'organizer');

export interface Meeting {
  id: string;
  title: string;
  description?: string | null;
  scheduledStartTime: string;
  scheduledEndTime: string;
  location?: string | null;
  organizerEmail?: string;
  participants?: MeetingParticipant[];
  joinUrl?: string;
  documentPackageId?: string | null;
  calendarEventId?: string | null;
  recurringSeriesId?: string | null;
  rawMsGraphMeeting?: Record<string, unknown>;
}

export interface ExportData {
  meeting: Meeting;
  transcripts: ParsedTranscriptEntry[];
  summary: MeetingSummary | null;
  actionItems: ActionItem[];
  agreements: MeetingAgreement[];
}

// ===== Shared Types =====

/**
 * Column horizontal alignment for tables in exports.
 * Used by PDF, DOCX, and Excel export functions.
 */
export type ColumnHAlign = 'left' | 'center' | 'right';

// ===== Shared Helper Functions =====

/**
 * Convert string to Title Case
 */
export const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Sanitize filename for export - removes/replaces problematic characters
 * while preserving Unicode word characters for non-Latin languages
 */
export const sanitizeFilename = (filename: string): string => {
  // Only strip characters that are invalid in filenames. Unlike `\w` (which is
  // ASCII-only), this preserves non-Latin letters so titles in Chinese, Arabic,
  // etc. survive instead of collapsing into underscores.
  const sanitized = filename
    .replace(/\s/g, '_') // Whitespace -> underscore
    .replace(/[<>:"|?*/\\]/g, '_') // OS-invalid characters -> underscore
    .replace(/_+/g, '_') // Collapse repeats
    .replace(/^_+|_+$/g, ''); // Trim leading/trailing underscores

  return sanitized.length > 0 ? sanitized : 'meeting';
};

/**
 * Canonical regex source for date patterns in meeting titles
 * Matches: DD-MM-YYYY, MM-DD-YYYY, YYYY-MM-DD (with - or _ separators)
 */
const DATE_IN_TITLE_REGEX_SOURCE = '(?:\\d{2}[-_]\\d{2}[-_]\\d{4}|\\d{4}[-_]\\d{2}[-_]\\d{2})';

/**
 * Regex pattern to detect dates in meeting titles (e.g., "12-02-2026" or "2026-02-12")
 */
export const DATE_IN_TITLE_PATTERN = new RegExp(DATE_IN_TITLE_REGEX_SOURCE);

/**
 * Regex pattern for stripping dates with surrounding whitespace from titles
 */
const DATE_WITH_WHITESPACE_PATTERN = new RegExp(`\\s*${DATE_IN_TITLE_REGEX_SOURCE}\\s*`, 'g');

/**
 * Check if a title already contains a date pattern
 */
export const titleHasDate = (title: string): boolean => {
  return DATE_IN_TITLE_PATTERN.test(title);
};

/**
 * Normalize a date string to UTC by appending 'Z' if it lacks timezone info
 */
export const normalizeToUtcDate = (dateTimeString: string | null | undefined): Date | null => {
  if (!dateTimeString) {
    return null;
  }
  // Detect if the string already includes timezone information (Z or +HH:MM/-HH:MM)
  const hasTimeZone = /Z$|[+-]\d{2}:?\d{2}$/.test(dateTimeString);
  const normalized = hasTimeZone ? dateTimeString : `${dateTimeString}Z`;
  return new Date(normalized);
};

/**
 * Generate the date suffix for export filenames
 * Returns empty string if title already contains a date, otherwise returns formatted date
 * Accepts either a Date object or a date string (which will be normalized to UTC)
 */
export const getExportDateSuffix = (title: string, startDate: Date | string): string => {
  if (titleHasDate(title)) {
    return '';
  }

  // Normalize to Date if string, ensuring UTC timezone
  const normalizedDate = typeof startDate === 'string' ? normalizeToUtcDate(startDate) : startDate;

  if (!normalizedDate) {
    return '';
  }

  return `_${normalizedDate.toISOString().split('T')[0]}`;
};

/**
 * Remove date patterns from a string (e.g., "10-02-2026" or "2026-02-10")
 * Useful for cleaning meeting titles that already have dates in them
 */
export const stripDateFromTitle = (title: string): string => {
  return title
    .replace(DATE_WITH_WHITESPACE_PATTERN, ' ')
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Clean description text by removing inline citations and markdown
 */
export const cleanDescription = (text: string | undefined | null): string => {
  if (!text) return '';
  return text
    .replace(/\[([^\]]+\.(?:pdf|docx?|xlsx?)[^\]]*)\]/gi, '') // Remove inline citations
    .replace(/^#{1,6}\s+/gm, '') // Remove markdown headers
    .replace(/^\*\s+/gm, '• ') // Convert markdown bullets
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markers but keep text
    .replace(/\*(.+?)\*/g, '$1') // Remove italic markers but keep text
    .replace(/\*\*/g, '') // Remove any remaining bold markers
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Format meeting date for display in exports
 */
export const formatMeetingDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Format linked agreements for document contradictions
 * Each agreement shows with date prefix and separated by line breaks
 */
export const formatLinkedAgreements = (
  agreementLinks: DocumentContradiction['agreementLinks'],
): string => {
  if (!agreementLinks || agreementLinks.length === 0) return '';
  return agreementLinks
    .map((link) => {
      const date = formatMeetingDate(link.meetingDatetime);
      const text = link.agreementText || '';
      return date ? `[${date}] ${text}` : text;
    })
    .filter(Boolean)
    .join('\n\n');
};

/**
 * Format linked agreements for agreement contradictions
 * Each agreement shows with date prefix and separated by line breaks
 */
export const formatAgreementContradictionLinks = (
  agreementLinks: AgreementContradiction['agreementLinks'],
): string => {
  if (!agreementLinks || agreementLinks.length === 0) return '';
  return agreementLinks
    .map((link) => {
      const date = formatMeetingDate(link.meetingDatetime);
      const text = link.agreementText || '';
      return date ? `[${date}] ${text}` : text;
    })
    .filter(Boolean)
    .join('\n\n');
};

/**
 * Format document sources from chunk links
 * Shows filename and page numbers
 */
export const formatDocumentSources = (chunkLinks: DocumentContradiction['chunkLinks']): string => {
  if (!chunkLinks || chunkLinks.length === 0) return '';
  return chunkLinks
    .map((link) => {
      const fileName = link.fileName || 'Unknown document';
      const pages =
        link.pageNumbers && link.pageNumbers.length > 0
          ? `Page ${link.pageNumbers.join(', ')}`
          : '';
      return pages ? `${fileName}, ${pages}` : fileName;
    })
    .filter(Boolean)
    .join('; ');
};
